import { Product, ProductFormData, ProductPresentation } from '@/pages/products/types/product.types';
import { authFetch } from './api.service';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

export interface ProductFilters {
  search?: string;
  category?: string;
  sortBy?: 'name' | 'category' | 'price' | 'popularity';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  includeInactive?: boolean;
}

export interface ProductStats {
  totalItems: number;
  totalActiveItems: number;
  totalInactiveItems: number;
  categories: Array<{ category: string; count: number }>;
}

export interface ProductResponse {
  success: boolean;
  data: Product[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  stats: ProductStats;
}

export interface CreateProductData extends ProductFormData {
  basePrice?: number;
  baseCost?: number;
  preparationTime?: number;
  image?: string;
  ingredients?: string[];
  allergens?: string[];
  calories?: number;
  presentations?: Array<{
    name: string;
    description?: string;
    price: number;
    cost?: number;
    isActive?: boolean;
  }>;
}

export interface UpdatePresentationPriceData {
  price: number;
  effectiveDate: string;
  reason?: string;
}

class ProductsService {
  private buildQueryParams(filters: ProductFilters): string {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.category && filters.category !== 'all') params.append('category', filters.category);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.includeInactive) params.append('includeInactive', 'true');

    return params.toString();
  }

  /**
   * Obtener todos los productos con filtros y paginación
   */
  async getProducts(filters: ProductFilters = {}): Promise<ProductResponse> {
    const queryParams = this.buildQueryParams(filters);
    const url = `${API_BASE_URL}/products${queryParams ? `?${queryParams}` : ''}`;

    const response = await authFetch(url, {
      method: 'GET'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener productos');
    }

    return await response.json();
  }

  /**
   * Obtener un producto por ID
   */
  async getProductById(id: string): Promise<{ success: boolean; data: Product }> {
    const response = await authFetch(`${API_BASE_URL}/products/${id}`, {
      method: 'GET'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener producto');
    }

    return await response.json();
  }

  /**
   * Crear nuevo producto
   */
  async createProduct(data: CreateProductData): Promise<{ success: boolean; data: Product; message: string }> {
    const response = await authFetch(`${API_BASE_URL}/products`, {
      method: 'POST',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al crear producto');
    }

    return await response.json();
  }

  /**
   * Actualizar producto existente
   */
  async updateProduct(id: string, data: Partial<CreateProductData>): Promise<{ success: boolean; data: Product; message: string }> {
    const response = await authFetch(`${API_BASE_URL}/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al actualizar producto');
    }

    return await response.json();
  }

  /**
   * Eliminar producto (soft delete)
   */
  async deleteProduct(id: string): Promise<{ success: boolean; message: string }> {
    const response = await authFetch(`${API_BASE_URL}/products/${id}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al eliminar producto');
    }

    return await response.json();
  }

  /**
   * Crear nueva presentación para un producto
   */
  async createPresentation(productId: string, data: {
    name: string;
    description?: string;
    price: number;
    cost?: number;
    isActive?: boolean;
    isDefault?: boolean;
  }): Promise<{ success: boolean; data: ProductPresentation; message: string }> {
    const response = await authFetch(`${API_BASE_URL}/products/${productId}/presentations`, {
      method: 'POST',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al crear presentación');
    }

    return await response.json();
  }

  /**
   * Actualizar presentación
   */
  async updatePresentation(productId: string, presentationId: string, data: {
    name?: string;
    description?: string;
    price?: number;
    cost?: number;
    isActive?: boolean;
    isDefault?: boolean;
  }): Promise<{ success: boolean; data: ProductPresentation; message: string }> {
    const response = await authFetch(`${API_BASE_URL}/products/${productId}/presentations/${presentationId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al actualizar presentación');
    }

    return await response.json();
  }

  /**
   * Eliminar presentación
   */
  async deletePresentation(productId: string, presentationId: string): Promise<{ success: boolean; message: string }> {
    const response = await authFetch(`${API_BASE_URL}/products/${productId}/presentations/${presentationId}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al eliminar presentación');
    }

    return await response.json();
  }

  /**
   * Actualizar precio de presentación
   */
  async updatePresentationPrice(
    productId: string,
    presentationId: string,
    data: UpdatePresentationPriceData
  ): Promise<{ success: boolean; data: any; message: string }> {
    const response = await authFetch(`${API_BASE_URL}/products/${productId}/presentations/${presentationId}/update-price`, {
      method: 'POST',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al actualizar precio');
    }

    return await response.json();
  }

  /**
   * Obtener historial de precios de una presentación
   */
  async getPresentationPriceHistory(productId: string, presentationId: string): Promise<{ success: boolean; data: any[] }> {
    const response = await authFetch(`${API_BASE_URL}/products/${productId}/presentations/${presentationId}/price-history`, {
      method: 'GET'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener historial de precios');
    }

    return await response.json();
  }

  /**
   * Obtener historial de precios de una presentación (ruta simplificada)
   */
  async getPresentationPriceHistoryById(presentationId: string): Promise<any[]> {
    const response = await authFetch(`${API_BASE_URL}/products/presentations/${presentationId}/price-history`, {
      method: 'GET'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener historial de precios');
    }

    return await response.json();
  }

  /**
   * Actualizar presentación (ruta simplificada)
   */
  async updatePresentationById(presentationId: string, data: Partial<ProductPresentation>): Promise<{ success: boolean; data: ProductPresentation }> {
    const response = await authFetch(`${API_BASE_URL}/products/presentations/${presentationId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al actualizar presentación');
    }

    return await response.json();
  }

  /**
   * Guardar receta para una presentación
   */
  async saveRecipe(presentationId: string, ingredients: Array<{
    ingredientId: string;
    quantity: string;
    notes?: string;
  }>): Promise<{ success: boolean; message: string; data: any }> {
    const response = await authFetch(`${API_BASE_URL}/products/presentations/${presentationId}/recipe`, {
      method: 'POST',
      body: JSON.stringify({ ingredients })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al guardar receta');
    }

    return await response.json();
  }

  /**
   * Obtener receta de una presentación
   */
  async getRecipe(presentationId: string): Promise<{ success: boolean; data: any }> {
    const response = await authFetch(`${API_BASE_URL}/products/presentations/${presentationId}/recipe`, {
      method: 'GET'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener receta');
    }

    return await response.json();
  }

  /**
   * Obtener categorías de productos
   */
  async getCategories(): Promise<{ success: boolean; data: string[] }> {
    try {
      // Por ahora devolver categorías estáticas, después se puede hacer dinámico
      const categories = [
        'sandwiches',
        'hamburguesas',
        'bebidas',
        'postres',
        'ensaladas',
        'combos',
        'otros'
      ];

      return {
        success: true,
        data: categories
      };
    } catch (error) {
      throw new Error('Error al obtener categorías');
    }
  }
}

export const productsService = new ProductsService();
